// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getProfile } from "../redux/slices/authSlice";
import { getWallet } from "../redux/slices/walletSlice";
import {
  getTransactions,
  getTransactionStatistics,
} from "../redux/slices/transactionSlice";
import { getKycStatus } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  TrendingUp,
  Wallet as WalletIcon,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  FileText,
  AlertCircle,
  Activity,
  Sparkles,
  BarChart3,
  Target,
  Zap,
  Users,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatFiat } from "../utils/currency";
import LoadingScreen from "../components/LoadingScreen";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wallet } = useSelector((state: RootState) => state.wallet);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions,
  );
  const { kyc } = useSelector((state: RootState) => state.kyc);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(getProfile() as any),
          dispatch(getWallet() as any),
          dispatch(getTransactions() as any),
          dispatch(getTransactionStatistics() as any),
          dispatch(getKycStatus() as any),
        ]);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]);

  const getVerificationStatus = (level: number) => {
    switch (level) {
      case 0:
        return {
          text: "Unverified",
          color: "text-rose-500",
          icon: <XCircle className="h-5 w-5" />,
          bg: "bg-rose-500/10",
        };
      case 1:
        return {
          text: "Email Verified",
          color: "text-amber-500",
          icon: <Clock className="h-5 w-5" />,
          bg: "bg-amber-500/10",
        };
      case 2:
        return {
          text: "KYC Submitted",
          color: "text-blue-500",
          icon: <FileText className="h-5 w-5" />,
          bg: "bg-blue-500/10",
        };
      case 3:
        return {
          text: "KYC Approved",
          color: "text-emerald-500",
          icon: <CheckCircle className="h-5 w-5" />,
          bg: "bg-emerald-500/10",
        };
      default:
        return {
          text: "Unknown",
          color: "text-gray-500",
          icon: <AlertCircle className="h-5 w-5" />,
          bg: "bg-gray-500/10",
        };
    }
  };

  const getKycStatusText = () => {
    if (!kyc || !kyc.status) return "Not Submitted";
    return kyc.status.charAt(0).toUpperCase() + kyc.status.slice(1);
  };

  // Get recent transactions
  const recentTransactions = transactions.slice(0, 5);

  // Calculate stats
  const totalDeposits = transactions
    .filter((t) => t.type === "deposit" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalWithdrawals = transactions
    .filter((t) => t.type === "withdrawal" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const pendingTransactions = transactions.filter(
    (t) => t.status === "pending",
  ).length;

  const successRate =
    transactions.length > 0
      ? Math.round(
          (transactions.filter((t) => t.status === "confirmed").length /
            transactions.length) *
            100,
        )
      : 0;

  if (loading) {
    return (
      <LoadingScreen message="Loading dashboard data..." />
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  Welcome back,{" "}
                  <span className="text-primary">{user?.first_name}!</span>
                </h1>
                <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Here's what's happening with your account today.
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-200/30 dark:border-gray-800/30">
              <div className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Account Active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Balance Card */}
        <div
          className={`${GlobalStyles.card} group hover:border-primary/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
              <WalletIcon className="h-6 w-6 text-primary" />
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-400" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Balance
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatFiat(user?.balance || 0)}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Available for withdrawal
            </p>
          </div>
        </div>

        {/* Deposits Card */}
        <div
          className={`${GlobalStyles.card} group hover:border-emerald-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-400/10">
              <ArrowDownRight className="h-6 w-6 text-emerald-500" />
            </div>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              {transactions.filter((t) => t.type === "deposit").length}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Deposits
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatFiat(totalDeposits)}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              All-time deposits
            </p>
          </div>
        </div>

        {/* Withdrawals Card */}
        <div
          className={`${GlobalStyles.card} group hover:border-rose-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-rose-500/20 to-rose-400/10">
              <ArrowUpRight className="h-6 w-6 text-rose-500" />
            </div>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400">
              {transactions.filter((t) => t.type === "withdrawal").length}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Withdrawals
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatFiat(totalWithdrawals)}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              All-time withdrawals
            </p>
          </div>
        </div>

        {/* Verification Card */}
        <div
          className={`${GlobalStyles.card} group hover:border-blue-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-400/10">
              <ShieldCheck className="h-6 w-6 text-blue-500" />
            </div>
            <div
              className={`px-2 py-1 rounded-full ${getVerificationStatus(user?.verification_level || 0).bg}`}
            >
              <span
                className={`text-xs font-medium ${getVerificationStatus(user?.verification_level || 0).color}`}
              >
                Lvl {user?.verification_level}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Verification Level
          </p>
          <div className="flex items-center gap-2">
            {getVerificationStatus(user?.verification_level || 0).icon}
            <p
              className={`text-xl font-bold ${getVerificationStatus(user?.verification_level || 0).color}`}
            >
              {getVerificationStatus(user?.verification_level || 0).text}
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Account security level
            </p>
          </div>
        </div>
      </div>

      {/* Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Transactions */}
        <div className="lg:col-span-2">
          <div className={`${GlobalStyles.card}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recent Transactions
              </h3>
              <Link
                to="/transactions"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/view`}
              >
                <span>View All</span>
                <ArrowUpRight className="h-3 w-3 ml-1 transition-transform group-hover/view:translate-x-0.5 group-hover/view:-translate-y-0.5" />
              </Link>
            </div>

            <div className="space-y-3">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200 group/transaction"
                  >
                    <div className="flex items-center">
                      <div
                        className={`p-2 rounded-lg mr-3 transition-transform group-hover/transaction:scale-110 ${
                          transaction.type === "deposit"
                            ? "bg-emerald-500/10 text-emerald-500"
                            : transaction.type === "withdrawal"
                              ? "bg-rose-500/10 text-rose-500"
                              : "bg-blue-500/10 text-blue-500"
                        }`}
                      >
                        {transaction.type === "deposit" ? (
                          <ArrowDownRight className="h-4 w-4" />
                        ) : transaction.type === "withdrawal" ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <CreditCard className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {transaction.type}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            transaction.created_at,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-medium ${
                          transaction.type === "deposit"
                            ? "text-emerald-500"
                            : transaction.type === "withdrawal"
                              ? "text-rose-500"
                              : "text-blue-500"
                        }`}
                      >
                        {transaction.type === "deposit" ? "+" : "-"}
                        {formatFiat(transaction.amount)}
                      </p>
                      <span
                        className={`${GlobalStyles.badge} ${
                          transaction.status === "confirmed"
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
                            : transaction.status === "pending"
                              ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                              : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800"
                        } text-xs mt-1 px-2 py-0.5`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <CreditCard className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    No transactions yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions & Stats */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className={`${GlobalStyles.card}`}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Link
                to="/deposit"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
              >
                <ArrowDownRight className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-y-0.5" />
                <span>Make Deposit</span>
              </Link>
              <Link
                to="/withdraw"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
              >
                <ArrowUpRight className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-y-0.5" />
                <span>Request Withdrawal</span>
              </Link>
              {user?.verification_level === 1 && !kyc && (
                <Link
                  to="/profile"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
                >
                  <Shield className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-y-0.5" />
                  <span>Complete KYC</span>
                </Link>
              )}
              <Link
                to="/recovery"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
              >
                <Shield className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-y-0.5" />
                <span>Recovery Services</span>
              </Link>
            </div>
          </div>

          {/* Mini Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`${GlobalStyles.card} !p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Active Today
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                    {
                      transactions.filter(
                        (t) =>
                          new Date(t.created_at).toDateString() ===
                          new Date().toDateString(),
                      ).length
                    }
                  </p>
                </div>
                <Activity className="h-5 w-5 text-primary" />
              </div>
            </div>

            <div className={`${GlobalStyles.card} !p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Success Rate
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                    {successRate}%
                  </p>
                </div>
                <Target className="h-5 w-5 text-emerald-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet & KYC Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Wallet Status */}
        <div className={`${GlobalStyles.card}`}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Wallet Status
          </h3>
          {wallet ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {wallet.wallet_name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Connected {new Date(wallet.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
                >
                  Connected
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Transactions
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {transactions.length}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Network
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    Multi-chain
                  </p>
                </div>
              </div>
              <Link
                to="/wallet"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full group/wallet`}
              >
                <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover/wallet:-translate-x-0.5" />
                <span>Manage Wallet</span>
              </Link>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <WalletIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Connect your wallet to start transactions
              </p>
              <Link
                to="/wallet"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/connect`}
              >
                <WalletIcon className="h-4 w-4 mr-2 transition-transform group-hover/connect:-translate-x-0.5" />
                <span>Connect Wallet</span>
              </Link>
            </div>
          )}
        </div>

        {/* KYC Status */}
        <div className={`${GlobalStyles.card}`}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            KYC Status
          </h3>
          {kyc ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900 dark:text-white">
                  Current Status
                </span>
                <span
                  className={`${GlobalStyles.badge} ${
                    kyc.status === "approved"
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
                      : kyc.status === "pending"
                        ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                        : kyc.status === "rejected"
                          ? "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800"
                          : ""
                  }`}
                >
                  {getKycStatusText()}
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Document Type:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">
                    {kyc.document_type?.replace("_", " ") || "N/A"}
                  </span>
                </div>
                {kyc.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Submitted:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(kyc.submitted_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {kyc.verified_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Verified:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(kyc.verified_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              {kyc.status === "pending" && (
                <div
                  className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4`}
                >
                  <Clock className="h-5 w-5 flex-shrink-0" />
                  <p className="text-sm">
                    Your KYC verification is under review. This usually takes
                    24-48 hours.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Complete KYC verification to unlock all platform features
              </p>
              {user?.verification_level === 1 && (
                <Link
                  to="/profile"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/kyc`}
                >
                  <Shield className="h-4 w-4 mr-2 transition-transform group-hover/kyc:-translate-x-0.5" />
                  <span>Start KYC Verification</span>
                </Link>
              )}
              {user?.verification_level === 0 && (
                <p className="text-sm text-amber-500">
                  Please verify your email first
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
